import { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoaderButton } from "@/components/LoaderButton";
import { useAuthUser } from "@/context/auth/AuthContext";
import useThermalPrint from "@/hooks/useThermalPrint";
import { generateTicketHTML } from "@/pdfs/generateTicketHTML";
import { pos } from "@/platform";
import type { PosVentaSinTicket } from "@/platform/types";
import { formatDecimalString } from "@/utils/formatCurrency";
import { ticketParaReimprimir } from "@/pages/orders/crear/quick/order-tab/ticket/ticketDeLaVenta";

// Una vez por negocio en cada apertura de la app: la pantalla se vuelve a
// montar con cada ruta.
const revisados = new Set<string>();

/**
 * La pantalla de recuperación (trd-client#338 AC-C). Si la PC se apagó entre
 * el commit y el ticket, al abrir la app muestra las ventas que quedaron sin
 * ticket y las reimprime. No se vuelven a cobrar: en el laboratorio, sin esta
 * pantalla, 30 cortes de la app dejaron 3 ventas duplicadas.
 */
function RecuperacionDeTickets() {
  const businessId = useAuthUser()?.businessId as string | undefined;
  const { printTicket, isLoaded } = useThermalPrint();
  const [ventas, setVentas] = useState<PosVentaSinTicket[]>([]);
  const [imprimiendo, setImprimiendo] = useState<string | null>(null);

  useEffect(() => {
    if (!pos.isAvailable || !businessId || revisados.has(businessId)) return;
    revisados.add(businessId);
    pos
      .unprintedSales(businessId)
      .then(setVentas)
      .catch(() => {
        // Sin almacén no hay nada que recuperar.
      });
  }, [businessId]);

  if (ventas.length === 0) return null;

  const reimprimir = async (venta: PosVentaSinTicket) => {
    setImprimiendo(venta.saleId);
    try {
      const ticket = ticketParaReimprimir(venta);
      await printTicket({
        ticketData: ticket.termica,
        htmlContent: generateTicketHTML(ticket.html),
      });
      await pos.markPrinted(venta.saleId);
      setVentas((actuales) =>
        actuales.filter((v) => v.saleId !== venta.saleId)
      );
    } finally {
      setImprimiendo(null);
    }
  };

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && setVentas([])}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ventas que quedaron sin ticket</DialogTitle>
          <DialogDescription className="text-pretty">
            Se registraron en la caja, pero el ticket no llegó a salir: se cerró
            la app o se cortó la luz. Reimprimilo; la venta ya está cobrada y no
            se vuelve a cobrar.
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-2">
          {ventas.map((venta) => (
            <li
              key={venta.saleId}
              className="flex items-center justify-between gap-3 rounded border border-border px-3 py-2"
            >
              <div className="flex flex-col">
                <span className="font-medium">{venta.ticket}</span>
                <span className="text-xs text-text-secondary tabular-nums">
                  {format(new Date(venta.occurredAt), "dd/MM HH:mm")} ·{" "}
                  {formatDecimalString(venta.total)}
                </span>
              </div>
              <LoaderButton
                variant="outline"
                isLoading={imprimiendo === venta.saleId}
                disabled={!isLoaded || imprimiendo !== null}
                onClick={() => void reimprimir(venta)}
              >
                Reimprimir
              </LoaderButton>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="muted" onClick={() => setVentas([])}>
            Después
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RecuperacionDeTickets;
